import { PureComponent, createRef } from 'react';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import ShareInput from '../ShareInput/ShareInput';
import { Controllable } from '../Controllable';
import partial from 'react-utils/partial';
import { getEmptyGroup, getNoMatchesOption, getSearchRegExp } from './Typeahead.utils';
import { TypeaheadContext } from './TypeaheadContext';
import { ScreenReaderOnly } from '../ScreenReaderOnly';
import { TypeaheadResultsGroup } from './TypeaheadResultsGroup';
import { TypeaheadResults } from './TypeaheadResults';
import { TypeaheadSearch } from './TypeaheadSearch';
import { TypeaheadResultsItem } from './TypeaheadResultsItem';
import { withTokenProps } from '../../../utils/withTokenProps';
import styledWithTheme from 'foundations-theming/utils';
import { DROPDOWN_MENU_HEIGHT } from '../../constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledGroupedTypeahead = styledWithTheme.div(({
  $cssStyles
}) => {
  return Object.assign({}, $cssStyles);
});

/** @internal */

/** @internal */

const getFilteredOptions = (options, searchRegex) => {
  if (!searchRegex) {
    return options;
  }
  return options.filter(({
    dropdownText,
    text,
    value
  }) => text && searchRegex.test(text) || searchRegex.test(`${value}`) || typeof dropdownText === 'string' && searchRegex.test(dropdownText));
};
const getFirstValidOption = (group, searchRegex) => {
  const options = getFilteredOptions(group.options, searchRegex);
  if (!options) {
    return null;
  }
  for (let i = 0; i < options.length; i++) {
    if (!options[i].disabled) {
      return options[i];
    }
  }
  return null;
};
const getInitialHighlighted = props => {
  const {
    options,
    value
  } = props;
  const groupLength = options.length;
  if (value) {
    for (let i = 0; i < groupLength; i++) {
      const group = props.options[i];
      for (let j = 0; j < group.options.length; j++) {
        const option = group.options[j];
        if (option.value === value) {
          return option;
        }
      } // end for each option
    } // end for each group
  }
  for (let i = 0; i < groupLength; i++) {
    const highlighted = getFirstValidOption(options[i]);
    if (highlighted) {
      return highlighted;
    }
  }
  return null;
};
export class GroupedTypeaheadComponent extends PureComponent {
  static getDerivedStateFromProps({
    autoFilter,
    search = ''
  }, {
    prevSearch
  }) {
    if (autoFilter && search !== prevSearch) {
      return {
        searchRegex: getSearchRegExp(search),
        prevSearch: search
      };
    } else if (search !== prevSearch) {
      return {
        prevSearch: search
      };
    }
    return null;
  }
  constructor(props) {
    super(props);
    this.handleChange = (option, evt) => {
      if (option.disabled) {
        return;
      }
      this.handleHighlight(option);
      const {
        onChange
      } = this.props;
      if (onChange && option.value) {
        onChange(syntheticEvent(option.value, evt));
      }
    };
    this.handleHighlight = (option, resetScroll) => {
      if (this.state.highlighted === option) {
        return;
      }
      if (resetScroll) {
        const highlightedNode = this._highlightedRef.current;
        if (highlightedNode != null) {
          highlightedNode.parentElement.parentElement.scrollTop = 0;
        }
      }
      this.setState({
        highlighted: option
      });
    };
    this.handleKeyDown = evt => {
      const index = this.getHighlightedIndexes();
      switch (evt.key) {
        case 'ArrowLeft':
          evt.preventDefault();
          this.keyLeft(index);
          break;
        case 'ArrowRight':
          evt.preventDefault();
          this.keyRight(index);
          break;
        case 'ArrowUp':
          evt.preventDefault();
          this.keyUp(index);
          break;
        case 'ArrowDown':
          evt.preventDefault();
          this.keyDown(index);
          break;
        case 'Enter':
          evt.preventDefault();
          this.keyEnter();
          break;
        default:
          break;
      }
    };
    this.state = {
      highlighted: getInitialHighlighted(props),
      prevSearch: props.search,
      searchRegex: props.search && props.autoFilter ? getSearchRegExp(props.search) : undefined
    };
    this._highlightedRef = /*#__PURE__*/createRef();
  }
  componentDidUpdate() {
    const highlightedNode = this._highlightedRef.current;
    if (highlightedNode == null) {
      return;
    }
    const parent = highlightedNode.parentElement.parentElement;
    const offsetTop = highlightedNode.offsetTop;
    const scrollTop = parent.scrollTop;
    if (scrollTop > offsetTop) {
      parent.scrollTop = 0;
      return;
    }
    const nodeBottom = highlightedNode.offsetHeight + offsetTop;
    const parentBottom = scrollTop + parent.offsetHeight;
    if (parentBottom < nodeBottom) {
      parent.scrollTop = scrollTop + nodeBottom - parentBottom;
    }
  }
  getEmptyGroupOption(group) {
    if (group.emptyMessage) {
      return Object.assign({}, getEmptyGroup(), {
        text: group.emptyMessage
      });
    }
    return getEmptyGroup();
  }
  getHighlightedIndexes() {
    const {
      options
    } = this.props;
    const {
      highlighted
    } = this.state;
    for (let i = 0; i < options.length; i++) {
      const group = options[i];
      for (let j = 0; j < group.options.length; j++) {
        const groupOption = group.options[j];
        if (highlighted === groupOption) {
          return {
            groupIdx: i,
            optionIdx: j
          };
        }
      } // end for each option
    } // end for each group

    return {};
  }
  getSearchCount() {
    const count = this.props.options.reduce((cnt, group) => {
      return Math.max(cnt, group.options.length);
    }, 0);
    return count;
  }
  isHighlighted(option) {
    const highlighted = this.state.highlighted;
    if (highlighted) {
      return option === highlighted;
    }
    const value = option.value;
    if (Object.prototype.hasOwnProperty.call(this.props, 'value')) {
      return this.props.value === value;
    }
    return this.state.value === value;
  }
  keyDown({
    groupIdx,
    optionIdx
  }) {
    if (!optionIdx || !groupIdx) return;
    const group = this.props.options[groupIdx];
    const optionLength = group.options.length;
    let optIdx = optionIdx + 1;
    while (optIdx < optionLength) {
      const option = group.options[optIdx];
      if (option.disabled) {
        optIdx++;
      } else {
        this.handleHighlight(option, false);
        break;
      }
    }
  }
  keyEnter() {
    if (this.state.highlighted) {
      this.handleChange(this.state.highlighted);
    }
  }
  keyLeft({
    groupIdx
  }) {
    if (!groupIdx) return;
    let grpIdx = groupIdx - 1;
    while (grpIdx >= 0) {
      const group = this.props.options[grpIdx];
      const option = getFirstValidOption(group, this.state.searchRegex);
      if (option) {
        this.handleHighlight(option, true);
        break;
      }
      grpIdx--;
    }
  }
  keyRight({
    groupIdx
  }) {
    if (!groupIdx) return;
    const groupLength = this.props.options.length;
    let grpIdx = groupIdx + 1;
    while (grpIdx < groupLength) {
      const group = this.props.options[grpIdx];
      const option = getFirstValidOption(group, this.state.searchRegex);
      if (option) {
        this.handleHighlight(option, true);
        break;
      }
      grpIdx++;
    }
  }
  keyUp({
    groupIdx,
    optionIdx
  }) {
    if (!optionIdx || !groupIdx) return;
    const group = this.props.options[groupIdx];
    let optIdx = optionIdx - 1;
    while (optIdx >= 0) {
      const option = group.options[optIdx];
      if (option.disabled) {
        optIdx--;
      } else {
        this.handleHighlight(option, false);
        break;
      }
    }
  }
  renderResultGroups() {
    const groups = [];
    this.props.options.forEach((group, i) => {
      let results = null;
      if (group.options.length === 0) {
        results = this.renderResultsEmpty(this.getEmptyGroupOption(group), !!group.emptyMessage);
      } else {
        const options = getFilteredOptions(group.options, this.state.searchRegex);
        if (options.length === 0) {
          results = this.renderResultsEmpty(getNoMatchesOption());
        } else {
          results = options.map(option => this.renderResultsItem(option));
        }
      }
      groups.push( /*#__PURE__*/_jsx(TypeaheadResultsGroup, {
        group: group,
        children: results
      }, String(group.value || i)));
    });
    return groups;
  }
  renderResultsItem(option) {
    const isHighlighted = this.isHighlighted(option);
    const {
      searchRegex
    } = this.state;
    return /*#__PURE__*/_jsx(TypeaheadResultsItem, {
      highlighted: isHighlighted,
      onClick: partial(this.handleChange, option),
      onMouseEnter: partial(this.handleHighlight, option, false),
      option: option,
      ref: isHighlighted ? this._highlightedRef : undefined,
      search: searchRegex
    }, String(option.value));
  }
  renderResultsEmpty(option, isMessage) {
    return /*#__PURE__*/_jsx(TypeaheadResultsItem, {
      message: isMessage,
      option: option
    }, String(option.value));
  }
  render() {
    const {
      inputRef,
      minimumSearchCount,
      onSearchChange,
      placeholder,
      search,
      cssStyles
    } = this.props;
    const hideSearch = minimumSearchCount && this.getSearchCount() < minimumSearchCount;
    const renderedSearch = /*#__PURE__*/_jsx(TypeaheadSearch, {
      inputRef: inputRef,
      onChange: onSearchChange,
      onKeyDown: this.handleKeyDown,
      placeholder: placeholder,
      value: search
    });
    return /*#__PURE__*/_jsx(TypeaheadContext.Provider, {
      value: {
        typeaheadResultsStyleOverrides: {
          display: 'flex',
          overflowY: 'hidden'
        },
        typeaheadResultsGroupStyleOverrides: {
          flexBasis: 1,
          flexGrow: 1,
          overflowX: 'hidden',
          overflowY: 'auto',
          maxHeight: DROPDOWN_MENU_HEIGHT
        }
      },
      children: /*#__PURE__*/_jsxs(StyledGroupedTypeahead, {
        $cssStyles: cssStyles,
        children: [hideSearch ? /*#__PURE__*/_jsx(ScreenReaderOnly, {
          children: renderedSearch
        }) : renderedSearch, /*#__PURE__*/_jsx(TypeaheadResults, {
          children: this.renderResultGroups()
        })]
      })
    });
  }
}
GroupedTypeaheadComponent.defaultProps = {
  autoFilter: true,
  minimumSearchCount: 0,
  search: ''
};
export const GroupedTypeahead = ShareInput(Controllable(withTokenProps(GroupedTypeaheadComponent), ['search', 'value']));
GroupedTypeahead.displayName = 'GroupedTypeahead';